/**
 * FCM API — Layer 1 of the three-layer pattern.
 *
 * Pure async functions, no TanStack, no React imports.
 */
import { apiClient } from "@/shared/lib/axios";
import { NOTIFICATIONS } from "@/shared/lib/apiPaths";

export interface FcmSubscribePayload {
  registrationId: string;
  type: "web" | "ios" | "android";
  deviceId?: string;
  name?: string;
}

export interface FcmSubscription {
  id: string;
  registrationId: string;
  deviceId: string;
  type: string;
  active: boolean;
  createdAt: string;
}

/** Runtime Firebase web config served by the backend (keeps secrets out of the bundle). */
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
}

/** GET /api/v1/notifications/firebase-config — Firebase web config incl. vapidKey. */
export async function fetchFirebaseConfig(): Promise<FirebaseConfig> {
  const { data } = await apiClient.get<FirebaseConfig>(`${NOTIFICATIONS}/firebase-config`);
  return data;
}

/** POST /api/v1/notifications/devices — register FCM device token. */
export async function subscribeToFcm(
  payload: FcmSubscribePayload
): Promise<{ id: string; active: boolean }> {
  const { data } = await apiClient.post<{ id: string; active: boolean }>(
    `${NOTIFICATIONS}/devices`,
    payload
  );
  return data;
}

/** POST /api/v1/notifications/devices/deactivate — deactivate FCM device. */
export async function unsubscribeFromFcm(registrationId: string): Promise<void> {
  await apiClient.post(`${NOTIFICATIONS}/devices/deactivate`, {
    registrationId,
  });
}

/**
 * GET /api/v1/notifications/devices — list FCM subscriptions.
 *
 * Backend FCMDeviceViewSet sets pagination_class=None, so the endpoint returns
 * a plain array. Older callers expected a paginated `{ results }` envelope, so
 * we accept both shapes to stay robust if pagination is ever re-enabled.
 */
export async function getFcmSubscriptions(signal?: AbortSignal): Promise<FcmSubscription[]> {
  try {
    const { data } = await apiClient.get<FcmSubscription[] | { results?: FcmSubscription[] }>(
      `${NOTIFICATIONS}/devices`,
      signal ? { signal } : undefined
    );
    if (Array.isArray(data)) return data;
    return data?.results ?? [];
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 401) return [];
    throw err;
  }
}